<script>
import {
  CdrSelect,
  CdrButton,
} from '@rei/cedar';

import AwardTile from './EditorsChoiceAwardResultTile.vue';
import { buildTrackingId, submitMetric, reInitAnalytics } from '../../../utils/analytics';

const awardLimitDefault = 9;
const awardLimitIncreaseIncrement = 9;

export default {
  name: 'EditorsChoiceAwardSearch',
  components: {
    AwardTile,
    CdrSelect,
    CdrButton,
  },
  props: {
    awardWinners: {
      type: Array,
      default: null,
    },
  },
  data: () => ({
    baseClassName: 'ea-editors-choice-award-search',
    selectedActivityForDropdown: '',
    hasFiltered: false,
    awardLimit: awardLimitDefault,
    allData: null,
    queryParamName: 'search',
  }),
  computed: {
    selectedActivityValidated() {
      if (this.availableActivityNames.indexOf(this.selectedActivityForDropdown) !== -1) {
        return this.selectedActivityForDropdown;
      }
      return '';
    },
    dropdownOptions() {
      const selectionOptions = [];
      if (this.availableActivityNames && this.availableActivityNames.length) {
        selectionOptions.unshift({
          text: 'All',
          value: '',
        });
        this.availableActivityNames.forEach((a) => {
          selectionOptions.push({
            text: a,
            value: a,
          });
        });
      }
      return selectionOptions;
    },
    availableActivityNames() {
      const activities = new Set();
      this.awardWinners.forEach((award) => {
        activities.add(award.metadata.activity);
      });

      return Array.from(activities).sort();
    },
    filteredAwards() {
      if (this.selectedActivityValidated) {
        return this.awardWinners.filter((award) => award.metadata.activity === this.selectedActivityValidated);
      }
      return this.awardWinners;
    },
    filteredAwardsLimited() {
      const filteredAwardsLimited = this.filteredAwards.map((a) => ({
        isVisible: false,
        award: a,
      }));
      filteredAwardsLimited.forEach((a, i) => {
        if (i < this.awardLimit) {
          // eslint-disable-next-line no-param-reassign
          a.isVisible = true;
        }
      });
      return filteredAwardsLimited;
    },
    isClickViewMoreResultsVisible() {
      return !this.hasFiltered
        || (this.awardLimit < this.filteredAwardsLimited.length);
    },
  },
  updated() {
    reInitAnalytics('#ea-editors-choice-award-search');
  },
  mounted() {
    const urlParams = new URLSearchParams(window.location.search);
    const param = urlParams.get(this.queryParamName);

    if (param) {
      this.selectedActivityForDropdown = param;
    }
  },
  methods: {
    selectionChanged() {
      this.hasFiltered = true;
      this.awardLimit = awardLimitDefault;

      this.updateURLQueryParam();

      try {
        const trackingId = buildTrackingId([
          'all awards',
          'filter',
          this.selectedActivityForDropdown,
        ]);
        submitMetric(trackingId);
      } catch (e) {
        // Failed to submit the analytics
        // Just silently let the error go through
      }
    },
    clickViewMoreResults() {
      if (!this.hasFiltered) {
        this.hasFiltered = true;
        this.awardLimit = awardLimitDefault;
      }
      this.awardLimit += awardLimitIncreaseIncrement;
    },
    updateURLQueryParam() {
      const urlParams = new URLSearchParams(window.location.search);
      let paramsString = window.location.pathname;

      if (this.selectedActivityForDropdown) {
        urlParams.set(this.queryParamName, this.selectedActivityForDropdown);
        paramsString += '?';
      } else {
        urlParams.delete(this.queryParamName);
      }

      paramsString += urlParams.toString();
      window.history.pushState(null, null, paramsString);
    },
  },
};
</script>

<template>
  <section
    id="ea-editors-choice-award-search"
    :class="`${baseClassName}`"
  >
    <div :class="`${baseClassName}__selector`">
      <cdr-select
        v-model="selectedActivityForDropdown"
        :options="dropdownOptions"
        background="primary"
        label="Filter by activity"
        size="small"
        @change="selectionChanged()"
      />
    </div>
    <div :class="`${baseClassName}__award_list`">
      <award-tile
        v-for="productAward in filteredAwardsLimited"
        v-show="productAward.isVisible"
        :key="productAward.award.name"
        data-test-id="award-tile"
        :award="productAward.award"
      />
    </div>
    <div :class="`${baseClassName}__actions_container`">
      <div :class="`${baseClassName}__actions`">
        <cdr-button
          v-if="isClickViewMoreResultsVisible"
          data-test-id="load-more-awards"
          :class="`${baseClassName}__action_button`"
          @click="clickViewMoreResults()"
        >
          Load More Awards
        </cdr-button>
        <cdr-button
          :class="`${baseClassName}__action_button`"
          modifier="secondary"
          tag="a"
          href="https://www.rei.com/s/rei-editors-choice-awards"
        >
          Shop All Award Winners
        </cdr-button>
      </div>
    </div>
  </section>
</template>

<style lang="scss">
@import '@rei/cdr-tokens/dist/rei-dot-com/scss/cdr-tokens.scss';

$baseClassName: 'ea-editors-choice-award-search';

.#{$baseClassName} {
  display: flex;
  flex-direction: column;
  margin-bottom: $cdr-space-three-x;

  @include cdr-sm-mq-up {
    @include cdr-container;
  }

  .#{$baseClassName}__award_list {
    margin: $cdr-space-two-x 0;
    display: flex;
    flex-wrap: wrap;
    gap: $cdr-space-two-x;

    @include cdr-xs-mq-only {
      gap: $cdr-space-quarter-x;
    }
    @include cdr-sm-mq-only {
      gap: $cdr-space-two-x;
      padding: 0 $cdr-space-one-x;
    }
  }

  .#{$baseClassName}__selector {
    margin: auto;
    width: 18em;
    select {
      background-color: $cdr-color-background-primary;
    }
  }

  .#{$baseClassName}__actions_container {
    align-items: center;
    display: flex;
    flex-direction: column;
  }

  .#{$baseClassName}__actions {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: $cdr-space-two-x;
  }

  .#{$baseClassName}__action_button {
    width: 100%;
    justify-content: center;
    padding: $cdr-space-three-quarter-x $cdr-space-three-x;
  }

}

</style>
